import { FieldLabel, StaticMultiCombobox } from '@myosh/odin-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { HierarchyType, HierarchyValue } from '../../../api/@types/hierarchy.types';
import { useHierarchyValuesQuery } from '../../../api/enhanced/enhanced-v4-api';

interface HierarchyTypeMultiSelectProps {
  type: HierarchyType;
  selectedIds: number[];
  onChange: (typeId: number, ids: number[]) => void;
}

/**
 * Renders a multi-select of the values for a single hierarchy type, using the shared
 * `StaticMultiCombobox` (as the dashboard hierarchy filter does). Fetches the type's full value set
 * (all pages) to populate the options and resolve the current selection's captions.
 */
function HierarchyTypeMultiSelect({ type, selectedIds, onChange }: HierarchyTypeMultiSelectProps) {
  const { t } = useTranslation();
  const { data: values = [], isFetching } = useHierarchyValuesQuery({ typeId: type.id });

  const selectedValues = useMemo<HierarchyValue[]>(
    () => values.filter((value) => selectedIds.includes(value.id)),
    [values, selectedIds]
  );

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel name={`hierarchy-filter-${type.id}`} label={type.caption} />
      <StaticMultiCombobox
        id={`hierarchy-filter-${type.id}`}
        items={values}
        value={selectedValues}
        onValueChange={(selected) =>
          onChange(
            type.id,
            selected.map((value) => value.id)
          )
        }
        getKey={(value) => value.id}
        getLabel={(value) => value.caption}
        placeholder={t('select-value', { value: type.caption })}
        isLoading={isFetching}
        showClear
      />
    </div>
  );
}

export default HierarchyTypeMultiSelect;
